import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Loader2, Plus, Check, Pencil, StickyNote } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/client';
import {
  Lead, LeadFollowup, FOLLOWUP_TYPES, dateTime, dateOnly, money, ratingClass,
} from './leads.api';
import { LeadContactButtons } from './LeadContactButtons';

/**
 * Everything known about one lead, in a side panel: the profile, its activity trail, and the
 * follow-ups planned against it.
 *
 * A drawer rather than a page because it is opened from the list, the pipeline and the calendar,
 * and losing the filtered list behind it every time is what makes people stop clicking through.
 */

const rowCls = 'flex justify-between gap-3 py-1 border-b border-dashed border-slate-100 last:border-0';

function Row({ label, value }: { label: string; value: any }) {
  if (value === null || value === undefined || value === '' || value === false) return null;
  return (
    <div className={rowCls}>
      <span className="text-xs text-gray-500">{label}</span>
      <span className="text-xs font-medium text-slate-800 text-right">{String(value)}</span>
    </div>
  );
}

export function LeadDetailDrawer({ leadId, onClose, onEdit }: { leadId: number; onClose: () => void; onEdit?: (l: Lead) => void }) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const [fu, setFu] = useState({ dueAt: '', type: 'CALL', notes: '' });

  const { data: lead, isLoading } = useQuery<Lead>({
    queryKey: ['lead', leadId],
    queryFn: async () => (await api.get<Lead>(`/leads/${leadId}`)).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['lead', leadId] });
    qc.invalidateQueries({ queryKey: ['leads'] });
    qc.invalidateQueries({ queryKey: ['lead-followups'] });
  };

  const addNote = useMutation({
    mutationFn: async () => (await api.post(`/leads/${leadId}/notes`, { text: note })).data,
    onSuccess: () => { setNote(''); toast.success('Note added'); refresh(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not add the note'),
  });

  const addFollowup = useMutation({
    mutationFn: async () => (await api.post(`/leads/${leadId}/followups`, fu)).data,
    onSuccess: () => { setFu({ dueAt: '', type: 'CALL', notes: '' }); toast.success('Follow-up planned'); refresh(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not plan the follow-up'),
  });

  const completeFollowup = useMutation({
    mutationFn: async (f: LeadFollowup) => (await api.patch(`/leads/followups/${f.id}`, { status: 'DONE' })).data,
    onSuccess: () => { toast.success('Marked done'); refresh(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not update'),
  });

  return (
    <div className="fixed inset-0 z-[880] flex justify-end bg-black/30" onClick={onClose}>
      <div className="w-full max-w-xl bg-white dark:bg-slate-900 h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {isLoading || !lead ? (
          <div className="p-10 text-center text-gray-400"><Loader2 className="animate-spin inline" /> Loading…</div>
        ) : (
          <>
            <div className="px-5 py-4 border-b sticky top-0 bg-white dark:bg-slate-900 z-10">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold">{lead.companyName}</h2>
                    {lead.status && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full border"
                        style={{ borderColor: lead.status.colour ?? '#cbd5e1', color: lead.status.colour ?? '#475569' }}>
                        {lead.status.name}
                      </span>
                    )}
                    {lead.rating && <span className={`text-[11px] px-2 py-0.5 rounded-full border ${ratingClass(lead.rating)}`}>{lead.rating}</span>}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {lead.leadNo} · {lead.contactName || 'No contact name'} {lead.mobile ? `· ${lead.mobile}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <LeadContactButtons leadId={lead.id} mobile={lead.mobile} company={lead.companyName} contactName={lead.contactName} onDone={refresh} size="md" />
                  {onEdit && (
                    <button onClick={() => onEdit(lead)} className="p-1.5 border rounded hover:bg-slate-50" title="Edit"><Pencil size={14} /></button>
                  )}
                  <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded"><X size={18} /></button>
                </div>
              </div>
              {lead.convertedMerchantId && (
                <div className="mt-2 text-[11px] px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 inline-block">
                  Signed up — merchant #{lead.convertedMerchantId}
                </div>
              )}
            </div>

            <div className="p-5 space-y-5">
              <section>
                <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Contact</h3>
                <Row label="Brand" value={lead.brandName} />
                <Row label="Contact" value={[lead.contactName, lead.designation].filter(Boolean).join(' · ')} />
                <Row label="Mobile" value={lead.mobile} />
                <Row label="Alt Mobile" value={lead.altMobile} />
                <Row label="Email" value={lead.email} />
                <Row label="Website" value={lead.website} />
                <Row label="Address" value={[lead.address, lead.city, lead.state, lead.pincode].filter(Boolean).join(', ')} />
                <Row label="GSTIN" value={lead.gstin} />
                <Row label="PAN" value={lead.pan} />
              </section>

              <section>
                <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Shipping profile</h3>
                <Row label="Service" value={lead.serviceType} />
                <Row label="Monthly orders" value={lead.monthlyOrders} />
                <Row label="Avg weight" value={lead.avgWeightKg ? `${lead.avgWeightKg} kg` : ''} />
                <Row label="Avg order value" value={lead.avgOrderValue ? money(lead.avgOrderValue) : ''} />
                <Row label="Category" value={lead.productCategory} />
                <Row label="Current courier" value={lead.currentCourier} />
                <Row label="Payment" value={[lead.paymentMode, lead.codPercent != null ? `${lead.codPercent}% COD` : ''].filter(Boolean).join(' · ')} />
                <Row label="Channel" value={lead.salesChannel} />
                <Row label="Pickup" value={[lead.pickupCity, lead.pickupPincode].filter(Boolean).join(' · ')} />
                <Row label="Pickup locations" value={lead.pickupLocations} />
                <Row label="Delivery zones" value={lead.deliveryZones} />
                <Row label="Expected rate" value={lead.expectedRate ? money(lead.expectedRate) : ''} />
                <Row label="Warehouse" value={lead.hasWarehouse ? 'Yes' : ''} />
                <Row label="Wants WMS" value={lead.wantsWms ? 'Yes' : ''} />
              </section>

              <section>
                <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Pipeline</h3>
                <Row label="Source" value={lead.source?.name} />
                <Row label="Sale person" value={lead.ownerName} />
                <Row label="Estimated value" value={lead.estimatedValue ? money(lead.estimatedValue) : ''} />
                <Row label="Expected close" value={lead.expectedCloseDate ? dateOnly(lead.expectedCloseDate) : ''} />
                <Row label="Next follow-up" value={lead.nextFollowupAt ? dateTime(lead.nextFollowupAt) : ''} />
                <Row label="Last contacted" value={lead.lastContactedAt ? dateTime(lead.lastContactedAt) : ''} />
                <Row label="Lost reason" value={lead.lostReason} />
                <Row label="Added" value={dateTime(lead.createdAt)} />
                {!!(lead.tags ?? []).length && (
                  <div className="flex flex-wrap gap-1 pt-2">
                    {lead.tags!.map((t) => (
                      <span key={t.id} className="text-[10px] px-2 py-0.5 rounded-full border"
                        style={{ borderColor: t.colour ?? '#cbd5e1', color: t.colour ?? '#475569' }}>{t.name}</span>
                    ))}
                  </div>
                )}
                {lead.notes && <p className="text-xs text-slate-600 mt-2 whitespace-pre-wrap bg-slate-50 dark:bg-slate-800 rounded p-2">{lead.notes}</p>}
                {!!lead.customFields && Object.keys(lead.customFields).length > 0 && (
                  <div className="mt-2">
                    {Object.entries(lead.customFields).map(([k, v]) => <Row key={k} label={k} value={typeof v === 'boolean' ? (v ? 'Yes' : '') : v} />)}
                  </div>
                )}
              </section>

              {/* --- follow-ups ------------------------------------------- */}
              <section>
                <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Follow-ups</h3>
                <div className="flex flex-wrap items-end gap-2 mb-2">
                  <input type="datetime-local" className="border rounded px-2 py-1 text-xs" value={fu.dueAt} onChange={(e) => setFu({ ...fu, dueAt: e.target.value })} />
                  <select className="border rounded px-2 py-1 text-xs" value={fu.type} onChange={(e) => setFu({ ...fu, type: e.target.value })}>
                    {FOLLOWUP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <input className="border rounded px-2 py-1 text-xs flex-1 min-w-[140px]" placeholder="Notes" value={fu.notes} onChange={(e) => setFu({ ...fu, notes: e.target.value })} />
                  <button disabled={!fu.dueAt || addFollowup.isPending} onClick={() => addFollowup.mutate()}
                    className="px-2 py-1 bg-emerald-600 text-white rounded text-xs inline-flex items-center gap-1 disabled:opacity-40">
                    <Plus size={12} /> Plan
                  </button>
                </div>
                <div className="space-y-1">
                  {(lead.followups ?? []).length === 0 && <p className="text-xs text-gray-400">Nothing planned yet.</p>}
                  {(lead.followups ?? []).map((f) => (
                    <div key={f.id} className="flex items-center justify-between gap-2 text-xs border rounded px-2 py-1">
                      <div>
                        <span className={f.status === 'PENDING' && new Date(f.dueAt) < new Date() ? 'text-rose-600 font-medium' : 'text-slate-700'}>
                          {dateTime(f.dueAt)}
                        </span>
                        <span className="text-gray-400"> · {f.type}</span>
                        {f.notes && <span className="text-gray-500"> — {f.notes}</span>}
                      </div>
                      {f.status === 'PENDING' ? (
                        <button onClick={() => completeFollowup.mutate(f)} className="px-1.5 py-0.5 border rounded text-emerald-700 border-emerald-200 hover:bg-emerald-50 inline-flex items-center gap-1">
                          <Check size={11} /> Done
                        </button>
                      ) : (
                        <span className="text-[10px] text-gray-400">{f.status}</span>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* --- activity --------------------------------------------- */}
              <section>
                <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Activity</h3>
                <div className="flex items-center gap-2 mb-2">
                  <input className="border rounded px-2 py-1 text-xs flex-1" placeholder="Add a note…" value={note}
                    onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && note.trim()) addNote.mutate(); }} />
                  <button disabled={!note.trim() || addNote.isPending} onClick={() => addNote.mutate()}
                    className="px-2 py-1 border rounded text-xs inline-flex items-center gap-1 disabled:opacity-40">
                    <StickyNote size={12} /> Save
                  </button>
                </div>
                <ol className="space-y-1">
                  {(lead.activities ?? []).map((a) => (
                    <li key={a.id} className="text-xs border-l-2 border-slate-200 pl-2">
                      <span className="text-slate-700">{a.message}</span>
                      <span className="text-gray-400"> · {dateTime(a.createdAt)}{a.userName ? ` · ${a.userName}` : ''}</span>
                    </li>
                  ))}
                  {(lead.activities ?? []).length === 0 && <li className="text-xs text-gray-400">No activity recorded.</li>}
                </ol>
              </section>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
